/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import React, { useRef } from 'react';
import { X_AXIS, Y_AXIS, Menu, MenuItem, Popover } from '@veeam/components';

// eslint-disable-next-line import/no-cycle
import { MenuItems } from '../MenuItems';
import { useContextMenuStore } from '../../store';

import type { SubmenuProps } from '../../interfaces';

export const Submenu: React.FC<SubmenuProps> = ({ id, text, isActive, disabled, hidden, icon, onClick }) => {
    const menuStore = useContextMenuStore();

    const currentItemRef = useRef(null);
    const submenuRef = useRef(null);

    if (hidden) {
        return null;
    }

    return (
        <>
            <MenuItem
                data-tid={`command-menu-submenu-${id}`}
                hasSubmenu={true}
                isActive={isActive}
                disabled={disabled}
                icon={icon}
                ref={currentItemRef}
                onMouseEnter={({ nativeEvent }) => {
                    nativeEvent.preventDefault();
                    nativeEvent.stopPropagation();
                    if (disabled) {
                        return;
                    }

                    menuStore.hideOtherSubmenus({ id });
                    menuStore.showSubmenu({ id });
                }}
                onMouseLeave={(event) => {
                    event.preventDefault();
                    event.stopPropagation();

                    if (submenuRef.current?.contains(event.relatedTarget as Node)) {
                        return;
                    }

                    if ((event.relatedTarget as Node).contains(submenuRef.current)) {
                        return;
                    }

                    menuStore.hideSubmenu({ id });
                }}
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();

                    if (onClick) {
                        menuStore.hideMenu();
                        onClick(event);
                    }
                }}
            >
                {text}
            </MenuItem>

            {isActive && (
                <Popover
                    target={{
                        measure: () => currentItemRef.current?.getBoundingClientRect(),
                    }}
                    config={[
                        {
                            xAxis: X_AXIS.outsideRight,
                            yAxis: Y_AXIS.insideTop,
                            offsetY: -1,
                        },
                        {
                            xAxis: X_AXIS.outsideRight,
                            yAxis: Y_AXIS.insideBottom,
                            offsetY: -1,
                        },
                        {
                            xAxis: X_AXIS.outsideLeft,
                            yAxis: Y_AXIS.insideTop,
                            offsetY: -1,
                        },
                        {
                            xAxis: X_AXIS.outsideLeft,
                            yAxis: Y_AXIS.insideBottom,
                            offsetY: -1,
                        },
                    ]}
                    render={popoverRef => (
                        <Menu ref={popoverRef}>
                            <MenuItems
                                ref={submenuRef}
                                parent={{ id }}
                            />
                        </Menu>
                    )}
                />
            )}
        </>
    );
};
